import type { LoginFlow, RegistrationFlow, RecoveryFlow, SettingsFlow, UiContainer, VerificationFlow } from "@ory/client-fetch";
import { Alert, Card, Spinner } from "@payezy/ui";
import { useEffect, useState, type ReactNode } from "react";
import { FlowForm, Messages } from "./FlowForm";
import { ResponseError, type FlowKind, type Kratos } from "./kratos";

type AnyFlow = LoginFlow | RegistrationFlow | RecoveryFlow | VerificationFlow | SettingsFlow;

export function AuthShell({ brand, children, footer }: { brand: ReactNode; children: ReactNode; footer?: ReactNode }) {
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center px-4 py-12">
      <div className="mb-8 text-xl font-bold tracking-tight">{brand}</div>
      <Card className="w-full max-w-sm p-6">{children}</Card>
      {footer && <div className="mt-6 text-center text-sm text-ink-500">{footer}</div>}
    </div>
  );
}

/**
 * Fetches the browser flow named by ?flow= and renders it. Missing or expired flows
 * restart by redirecting to Kratos, which bounces back with a fresh flow id.
 */
export function useFlow<T extends AnyFlow>(kratos: Kratos, kind: FlowKind, params: Record<string, string | undefined> = {}) {
  const [flow, setFlow] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const search = typeof window !== "undefined" ? window.location.search : "";
  const id = new URLSearchParams(search).get("flow");
  const paramsKey = JSON.stringify(params);

  useEffect(() => {
    let cancelled = false;
    if (!id) {
      window.location.replace(kratos.browserFlowUrl(kind, params));
      return;
    }
    kratos
      .getFlow(kind, id)
      .then((f) => !cancelled && setFlow(f as T))
      .catch(async (e: unknown) => {
        if (cancelled) return;
        if (e instanceof ResponseError) {
          const status = e.response.status;
          if (status === 410 || status === 404 || status === 403 || status === 400) {
            // Expired, unknown, or CSRF-mismatched flow: start over.
            let body: { redirect_browser_to?: string; error?: { id?: string } } | undefined;
            try {
              body = await e.response.json();
            } catch {
              body = undefined;
            }
            if (body?.redirect_browser_to) {
              window.location.replace(body.redirect_browser_to);
              return;
            }
            window.location.replace(kratos.browserFlowUrl(kind, params));
            return;
          }
        }
        setError("We couldn't load this page. Please try again.");
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, kind, paramsKey]);

  return { flow, error };
}

export function FlowBody({ ui, groups }: { ui: UiContainer; groups?: string[] }) {
  return (
    <div className="flex flex-col gap-4">
      <Messages messages={ui.messages} />
      <FlowForm ui={ui} groups={groups} />
    </div>
  );
}

export function FlowLoading({ error }: { error?: string | null }) {
  if (error) return <Alert tone="danger">{error}</Alert>;
  return (
    <div className="flex justify-center py-8">
      <Spinner />
    </div>
  );
}

export type { AnyFlow };
